import { Icon } from "@/components/Icon/Icon";
import { USAGE_META } from "../_model";
import type { SubstanceUsage } from "../_model";

interface UsageTagProps {
  id: SubstanceUsage;
  size?: "md" | "sm";
  muted?: boolean;
  iconOnly?: boolean;
}

export function UsageTag({ id, size = "md", muted, iconOnly }: UsageTagProps) {
  const m = USAGE_META[id];
  if (!m) return null;

  if (iconOnly) {
    return (
      <span
        title={`${m.label} — ${m.blurb}`}
        style={{
          width: 19,
          height: 19,
          borderRadius: 4,
          flexShrink: 0,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          background: m.bg,
          color: m.fg,
          border: `1px solid ${m.line}`,
        }}
      >
        <Icon name={m.icon as Parameters<typeof Icon>[0]["name"]} size={11} />
      </span>
    );
  }

  const sm = size === "sm";
  return (
    <span
      title={m.blurb}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: sm ? 4 : 5,
        padding: sm ? "1px 6px 1px 5px" : "2px 8px 2px 6px",
        borderRadius: 999,
        whiteSpace: "nowrap",
        background: muted ? "transparent" : m.bg,
        border: `1px solid ${muted ? "var(--line)" : m.line}`,
        color: muted ? "var(--ink-4)" : m.fg,
        fontSize: sm ? 10 : 11,
        fontWeight: 600,
        letterSpacing: "0.01em",
      }}
    >
      <span style={{ display: "inline-flex", opacity: 0.85 }}>
        <Icon name={m.icon as Parameters<typeof Icon>[0]["name"]} size={sm ? 9 : 10} />
      </span>
      {size === "sm" ? m.short : m.label}
    </span>
  );
}

interface UsageTagsProps {
  usage: SubstanceUsage[];
  size?: "md" | "sm";
  iconOnly?: boolean;
  order?: SubstanceUsage[];
}

export function UsageTags({
  usage,
  size = "md",
  iconOnly,
  order = ["ingredient", "target", "formulation"],
}: UsageTagsProps) {
  return (
    <span className="hstack" style={{ gap: iconOnly ? 6 : 5, flexWrap: "wrap" }}>
      {order
        .filter((u) => usage.includes(u))
        .map((u) => (
          <UsageTag key={u} id={u} size={size} iconOnly={iconOnly} />
        ))}
    </span>
  );
}
